import type { ProgressTree } from '@localagent/sdk';

/**
 * The run painted under the composer. A completed run stays on its assistant
 * message. Until this turn's own `reasoning_started` arrives there is no live
 * run — the previous one must not fill the gap (sdk ADR-042).
 */
export function selectLiveRun(runs: readonly ProgressTree[]): ProgressTree | null {
  let live: ProgressTree | null = null;
  for (const run of runs) {
    if (run.status === 'in_progress') live = run;
  }
  return live;
}

/** True while the user has sent and this turn has not opened a run yet. */
export function turnPending(runs: readonly ProgressTree[], isReasoning: boolean): boolean {
  return isReasoning && selectLiveRun(runs) == null;
}

/**
 * Which progress-tracker card starts open. A pending turn closes history so
 * the previous run is not presented as this turn. An open run is the live
 * card; at rest the latest completed run stays open.
 */
export function runIsDefaultOpen(
  run: ProgressTree,
  runs: readonly ProgressTree[],
  pendingTurn: boolean,
): boolean {
  if (pendingTurn) return false;
  if (run.status === 'in_progress') return true;
  if (runs.some((r) => r.status === 'in_progress')) return false;
  const last = runs[runs.length - 1];
  return last != null && run.runId === last.runId;
}

/** Events whose timestamps fall inside the run. A later turn must not hydrate an earlier one. */
export function eventsInRun<T extends { created_at: string }>(
  events: readonly T[],
  run: Pick<ProgressTree, 'startedAt' | 'endedAt'>,
): T[] {
  return events.filter((event) => {
    if (event.created_at < run.startedAt) return false;
    if (run.endedAt != null && event.created_at > run.endedAt) return false;
    return true;
  });
}
